import {
  ViewpointModeViewKey,
  WorkspaceModeViewKey,
  ViewpointModeViewSettingsShape,
  WorkspaceModeViewSettingsShape,
} from './types';
import {
  DEFAULT_VIEWPOINT_MODE_SETTINGS,
  DEFAULT_WORKSPACE_MODE_SETTINGS,
} from './defaultStreamState';
import { isViewpointModeView, isWorkspaceModeView } from './utils';

/**
 * Union of view keys that exist in both viewpoint mode and workspace mode
 */
type SharedViewKey = ViewpointModeViewKey & WorkspaceModeViewKey;

/**
 * Determines which view keys are allowed based on the mode
 * - When isViewpointMode is true: only ViewpointModeViewKey
 * - When isViewpointMode is false: only WorkspaceModeViewKey
 * - When isViewpointMode is unknown (boolean): keys that exist in both modes
 */
type ViewKeyForMode<TIsViewpointMode extends boolean> =
  TIsViewpointMode extends true
    ? ViewpointModeViewKey
    : TIsViewpointMode extends false
      ? WorkspaceModeViewKey
      : SharedViewKey;

/**
 * Returns the appropriate view settings type based on mode and view key
 * - When isViewpointMode is true: returns ViewpointModeViewSettingsShape[TViewKey]
 * - When isViewpointMode is false: returns WorkspaceModeViewSettingsShape[TViewKey]
 * - When isViewpointMode is unknown: returns union of both possible types
 */
type ViewSettingsForMode<
  TIsViewpointMode extends boolean,
  TViewKey extends ViewKeyForMode<TIsViewpointMode>,
> = TIsViewpointMode extends true
  ? ViewpointModeViewSettingsShape[TViewKey & ViewpointModeViewKey]
  : TIsViewpointMode extends false
    ? WorkspaceModeViewSettingsShape[TViewKey & WorkspaceModeViewKey]
    :
        | ViewpointModeViewSettingsShape[TViewKey & ViewpointModeViewKey]
        | WorkspaceModeViewSettingsShape[TViewKey & WorkspaceModeViewKey];

/**
 * Gets the default view settings for a given view ID and mode.
 *
 * If a viewId is allowed only in Viewpoint Mode, the function won't accept isViewpointMode
 * as a boolean parameter, only as a literal. Same goes for workspace-mode-only views.
 *
 * If the view exists in both viewpoint and workspace mode, isViewpointMode can be boolean.
 *
 * @template TViewKey - The view ID, constrained to valid keys for the given mode
 * @template TIsViewpointMode - Boolean indicating if in viewpoint mode
 * @param viewId - The ID of the view to get settings for
 * @param isViewpointMode - Whether the view is in viewpoint mode
 * @returns The default settings for the specified view and mode
 */
export function getDefaultViewSettings<
  TIsViewpointMode extends boolean,
  TViewKey extends ViewKeyForMode<TIsViewpointMode>,
>(
  viewId: TViewKey,
  isViewpointMode: TIsViewpointMode
): ViewSettingsForMode<TIsViewpointMode, TViewKey> {
  // TypeScript can't narrow the generic TIsViewpointMode from the runtime
  // `isViewpointMode` check, so the return type stays an unresolved conditional
  // that only a cast can satisfy. The `{}` fallback also covers the
  // type-impossible case where the mode flag doesn't match the view id.
  const defaultSettings =
    isViewpointMode && isViewpointModeView(viewId)
      ? DEFAULT_VIEWPOINT_MODE_SETTINGS[viewId]
      : !isViewpointMode && isWorkspaceModeView(viewId)
        ? DEFAULT_WORKSPACE_MODE_SETTINGS[viewId]
        : isViewpointModeView(viewId)
          ? DEFAULT_VIEWPOINT_MODE_SETTINGS[viewId]
          : isWorkspaceModeView(viewId)
            ? DEFAULT_WORKSPACE_MODE_SETTINGS[viewId]
            : {};
  return defaultSettings as ViewSettingsForMode<TIsViewpointMode, TViewKey>;
}
